package ai.wagemix.web.screens.mixbuilder

import ai.wagemix.logic.mixbuilder.WMMixBuilderPresenter.Prompt
import ai.wagemix.logic.mixbuilder.WMPremiumFeature
import ai.wagemix.web.components.WMButton
import ai.wagemix.web.components.WMButtonKind
import ai.wagemix.web.components.WMDialog
import ai.wagemix.web.components.WMDialogButtonStyle
import ai.wagemix.web.components.WMField
import ai.wagemix.web.components.WMTextField
import ai.wagemix.web.screens.WMWebStrings
import ai.wagemix.web.screens.planName
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.fsryan.foundation.logic.prompting.FSPromptingPresenter
import ai.wagemix.web.screens.WMPromptDialog
import com.varabyte.kobweb.silk.style.toModifier

/**
 * Renders the builder's prompts (Penpot `Dialog / Save mix`, `Update mix`, `Discard`, `Premium
 * feature`, `Subscription Ended`, `Sign in`, `Re-evaluate`); foundation prompts fall through to [FSBasePromptDialog]. [onConfirmSave]
 * carries the title the Save dialog collects.
 */
@Composable
fun <P: Prompt> WMMixBuilderPromptDialog(
    prompt: P,
    strings: WMWebStrings,
    onResponse: (Int) -> Unit,
    onConfirmSave: (String) -> Unit
) {
    val mb = strings.mixBuilder
    val j = strings.jobDescriptions
    val c = strings.common
    WMPromptDialog(prompt = prompt, unwrap = { (it as? Prompt.ComposedSuperPrompt)?.wrapped }, strings = strings, onResponse = onResponse) { p ->
        when (p) {
            is Prompt.SaveMix -> {
                var title by remember(p) { mutableStateOf(p.suggestedTitle) }
                WMDialog(
                    title = mb.saveDialogTitle,
                    description = mb.saveDialogDescription,
                    onDismiss = { onResponse(Prompt.SaveMix.Selection.Cancel.ordinal) },
                    forceInteraction = true,
                    actions = {
                        WMButton(c.cancel, kind = WMButtonKind.Ghost, modifier = WMDialogButtonStyle.toModifier()) { onResponse(Prompt.SaveMix.Selection.Cancel.ordinal) }
                        WMButton(mb.saveMix, modifier = WMDialogButtonStyle.toModifier(), enabled = title.isNotBlank()) { onConfirmSave(title) }
                    }
                ) {
                    WMField(mb.mixTitle, helperText = mb.mixTitleHelper) {
                        WMTextField(title, onTextChange = { title = it }, onCommit = { if (title.isNotBlank()) onConfirmSave(title) })
                    }
                }
            }
            is Prompt.UpdateMix -> WMDialog(
                title = mb.updateDialogTitle,
                description = "${mb.updateDialogDescription} (${p.title})",
                onDismiss = { onResponse(Prompt.UpdateMix.Selection.Cancel.ordinal) },
                forceInteraction = true,
                actions = {
                    WMButton(c.cancel, kind = WMButtonKind.Ghost, modifier = WMDialogButtonStyle.toModifier()) { onResponse(Prompt.UpdateMix.Selection.Cancel.ordinal) }
                    WMButton(mb.saveAsNew, kind = WMButtonKind.Secondary, modifier = WMDialogButtonStyle.toModifier()) { onResponse(Prompt.UpdateMix.Selection.SaveAsNew.ordinal) }
                    WMButton(mb.updateMix, modifier = WMDialogButtonStyle.toModifier()) { onResponse(Prompt.UpdateMix.Selection.Update.ordinal) }
                }
            ) {}
            is Prompt.DiscardDraft -> WMDialog(
                title = mb.discardDialogTitle,
                description = mb.discardDialogDescription,
                onDismiss = { onResponse(Prompt.DiscardDraft.Selection.Cancel.ordinal) },
                actions = {
                    WMButton(c.cancel, kind = WMButtonKind.Ghost, modifier = WMDialogButtonStyle.toModifier()) { onResponse(Prompt.DiscardDraft.Selection.Cancel.ordinal) }
                    WMButton(mb.discard, kind = WMButtonKind.Danger, modifier = WMDialogButtonStyle.toModifier()) { onResponse(Prompt.DiscardDraft.Selection.Discard.ordinal) }
                }
            ) {}
            is Prompt.PremiumFeature -> WMDialog(
                title = mb.premiumTitle,
                description = when (p.feature) {
                    WMPremiumFeature.Weights -> mb.premiumWeights
                    WMPremiumFeature.Filters -> mb.premiumFilters
                    WMPremiumFeature.Save -> mb.premiumSave
                    WMPremiumFeature.Share -> mb.premiumShare
                    WMPremiumFeature.AiMix -> mb.premiumAiMix
                },
                onDismiss = { onResponse(Prompt.PremiumFeature.Selection.Dismiss.ordinal) },
                actions = {
                    WMButton(c.dismiss, kind = WMButtonKind.Ghost, modifier = WMDialogButtonStyle.toModifier()) { onResponse(Prompt.PremiumFeature.Selection.Dismiss.ordinal) }
                    WMButton(c.viewPlans, modifier = WMDialogButtonStyle.toModifier()) { onResponse(Prompt.PremiumFeature.Selection.ViewPlans.ordinal) }
                }
            ) {}
            is Prompt.SubscriptionEnded -> WMDialog(
                title = strings.renewal.dialogTitle,
                description = strings.renewal.dialogMessage,
                onDismiss = { onResponse(Prompt.SubscriptionEnded.Selection.Dismiss.ordinal) },
                actions = {
                    WMButton(c.dismiss, kind = WMButtonKind.Ghost, modifier = WMDialogButtonStyle.toModifier()) {
                        onResponse(Prompt.SubscriptionEnded.Selection.Dismiss.ordinal)
                    }
                    val planName = p.plan?.let { strings.planName(it) }
                    val renew = planName?.let(strings.renewal.renewPlan) ?: strings.renewal.renew
                    WMButton(renew, modifier = WMDialogButtonStyle.toModifier()) {
                        onResponse(Prompt.SubscriptionEnded.Selection.Renew.ordinal)
                    }
                }
            ) {}
            is Prompt.SignInRequired -> WMDialog(
                title = mb.signInTitle,
                description = mb.signInMessage,
                onDismiss = { onResponse(Prompt.SignInRequired.Selection.Dismiss.ordinal) },
                actions = {
                    WMButton(c.dismiss, kind = WMButtonKind.Ghost, modifier = WMDialogButtonStyle.toModifier()) { onResponse(Prompt.SignInRequired.Selection.Dismiss.ordinal) }
                    WMButton(c.signIn, kind = WMButtonKind.Secondary, modifier = WMDialogButtonStyle.toModifier()) { onResponse(Prompt.SignInRequired.Selection.SignIn.ordinal) }
                    WMButton(c.register, modifier = WMDialogButtonStyle.toModifier()) { onResponse(Prompt.SignInRequired.Selection.Register.ordinal) }
                }
            ) {}
            is Prompt.ConfirmReevaluate -> WMDialog(
                title = j.reevaluateTitle,
                description = listOf(p.fileName, j.reevaluateDescription).filter { it.isNotBlank() }.joinToString(": "),
                onDismiss = { onResponse(Prompt.ConfirmReevaluate.Selection.Cancel.ordinal) },
                forceInteraction = true,
                actions = {
                    WMButton(c.cancel, kind = WMButtonKind.Ghost, modifier = WMDialogButtonStyle.toModifier()) { onResponse(Prompt.ConfirmReevaluate.Selection.Cancel.ordinal) }
                    WMButton(j.reevaluate, modifier = WMDialogButtonStyle.toModifier()) { onResponse(Prompt.ConfirmReevaluate.Selection.Reevaluate.ordinal) }
                }
            ) {}
            else -> Unit
        }
    }
}
